/**
 * Application providers.
 *
 * Query client configuration lives here so retry and caching behaviour is
 * decided once. Note the retry rule: client errors are never retried, because
 * repeating a 422 just wastes the donor's time and battery.
 */

import { useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { ApiRequestError } from '../../core/api/client';
import { LanguageProvider } from '../../core/i18n/LanguageProvider';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (error instanceof ApiRequestError && !error.isRetryable) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
      mutations: {
        // A failed write is never retried automatically: the donation and
        // payment endpoints are idempotent by key, but a silent retry would
        // still hide a real failure from the donor.
        retry: false,
      },
    },
  });
}

export function AppProviders({
  children,
  client,
}: {
  children: ReactNode;
  client?: QueryClient;
}) {
  const [queryClient] = useState(() => client ?? createQueryClient());

  return (
    <SafeAreaProvider>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>{children}</LanguageProvider>
      </QueryClientProvider>
    </SafeAreaProvider>
  );
}
